import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff, PhoneOutgoing, PhoneIncoming, Mic, MicOff, ShieldCheck, PhoneCall, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../auth/AuthContext.jsx';
import { auth } from '../auth/firebase.js';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';

const LAST_EMAIL_KEY = 'qubby-admin:lastEmail';
const REMEMBER_KEY = 'qubby-admin:rememberEmail';

// Staggered entry — hero / form 各自一段 group，子元素 0.08s 一拍
const heroContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
};
const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function LoginPage() {
    const { t } = useTranslation('login');
    const { user, loading, signInEmail } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const dest = loc.state?.from?.pathname || '/';

    const [email, setEmail] = useState(() =>
        localStorage.getItem(REMEMBER_KEY) === '1'
            ? localStorage.getItem(LAST_EMAIL_KEY) || ''
            : ''
    );
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(localStorage.getItem(REMEMBER_KEY) !== '0');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [resetMsg, setResetMsg] = useState(null);   // 忘記密碼結果
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        if (user) nav(dest, { replace: true });
    }, [user, nav, dest]);

    // 忘記密碼：用輸入的 email 寄 Firebase 密碼重設信（客戶自助）
    async function onForgotPassword() {
        setError(null); setResetMsg(null);
        const em = email.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setError(t('forgot.needEmail')); return; }
        setResetting(true);
        try {
            await sendPasswordResetEmail(auth, em);
            setResetMsg(t('forgot.sent', { email: em }));
        } catch (err) {
            // 不揭露帳號是否存在：user-not-found 也顯示成功文案
            const code = err.code || '';
            if (code.includes('user-not-found')) setResetMsg(t('forgot.sentMaybe', { email: em }));
            else if (code.includes('too-many')) setError(t('forgot.tooMany'));
            else setError(err.message);
        } finally {
            setResetting(false);
        }
    }

    async function onSubmit(e) {
        e.preventDefault();
        setError(null);
        setResetMsg(null);
        setSubmitting(true);
        try {
            const trimmedEmail = email.trim();
            const trimmedPassword = password.trim();
            await signInEmail(trimmedEmail, trimmedPassword);
            if (email !== trimmedEmail) setEmail(trimmedEmail);
            if (password !== trimmedPassword) setPassword(trimmedPassword);
            if (remember) {
                localStorage.setItem(LAST_EMAIL_KEY, trimmedEmail);
                localStorage.setItem(REMEMBER_KEY, '1');
            } else {
                localStorage.removeItem(LAST_EMAIL_KEY);
                localStorage.setItem(REMEMBER_KEY, '0');
            }
        } catch (err) {
            const code = err.code || '';
            if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
                setError(t('err.invalid'));
            } else if (code.includes('too-many')) {
                setError(t('err.tooMany'));
            } else {
                setError(err.message);
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-dvh bg-cream-50 relative overflow-hidden">
            {/* === Aurora gradient layer — 3 層 radial gradient drift，blend mode 疊出 SaaS premium 流動感 === */}
            <motion.div
                aria-hidden="true"
                className="absolute -top-40 -right-32 size-[720px] rounded-full blur-3xl pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 50% 50%, var(--color-accent-300) 0%, transparent 65%)',
                    opacity: 0.6,
                    mixBlendMode: 'multiply',
                }}
                animate={{
                    x: [0, 70, -40, 0],
                    y: [0, 50, -20, 0],
                    scale: [1, 1.12, 0.92, 1],
                }}
                transition={{ duration: 20, ease: 'easeInOut', repeat: Infinity }}
            />
            <motion.div
                aria-hidden="true"
                className="absolute -bottom-52 -left-40 size-[660px] rounded-full blur-3xl pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 50% 50%, var(--color-cream-200) 0%, transparent 60%)',
                    opacity: 0.8,
                    mixBlendMode: 'screen',
                }}
                animate={{
                    x: [0, -50, 35, 0],
                    y: [0, -35, 50, 0],
                    scale: [1, 1.08, 0.94, 1],
                }}
                transition={{ duration: 26, ease: 'easeInOut', repeat: Infinity }}
            />
            <motion.div
                aria-hidden="true"
                className="absolute size-[520px] rounded-full blur-3xl pointer-events-none"
                style={{
                    top: '35%',
                    left: '28%',
                    background:
                        'radial-gradient(circle at 50% 50%, rgba(99,141,168,0.28) 0%, transparent 70%)',
                    mixBlendMode: 'multiply',
                }}
                animate={{
                    x: [0, -60, 40, 0],
                    y: [0, 35, -25, 0],
                    scale: [1, 1.1, 0.95, 1],
                }}
                transition={{ duration: 32, ease: 'easeInOut', repeat: Infinity }}
            />

            {/* === Subtle dot grid texture — SaaS 標配紋理 === */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-[0.05]"
                style={{
                    backgroundImage:
                        'radial-gradient(circle, var(--color-ink-900) 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                }}
            />

            {/* === Top operator status bar — 全寬 system console 風格 === */}
            <TopStatusBar />


            {/* === Floating call cards — outbound + inbound 浮在 brand 文字「後面」當深度層 === */}
            {/* z-index 0，opacity 60%，brand 文字在前方蓋過 — operator console 氛圍感裝飾 */}
            <FloatingCallCard
                direction="outbound"
                position={{ left: '38%', top: '10%' }}
                rotate={-3}
                z={0}
                delay={0.55}
                muted
            />
            <FloatingCallCard
                direction="inbound"
                position={{ left: '40%', top: '55%' }}
                rotate={3}
                z={0}
                delay={0.95}
                muted
            />

            <div className="relative grid lg:grid-cols-[1.45fr_1fr] min-h-dvh pt-12" style={{ zIndex: 5 }}>
                {/* LEFT — editorial brand panel */}
                <motion.div
                    className="hidden lg:flex flex-col justify-between px-16 py-14 relative z-10"
                    variants={heroContainer}
                    initial="hidden"
                    animate="show"
                >
                    <motion.div
                        variants={fadeUp}
                        className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 inline-flex items-center gap-2.5"
                    >
                        {/* Signal bars EQ — 5 條 oscillating 像撥號波形 */}
                        <span className="inline-flex items-end gap-[3px] h-3">
                            {[0, 0.12, 0.24, 0.36, 0.48].map((delay, i) => (
                                <motion.span
                                    key={i}
                                    className="w-[2px] bg-accent-500 rounded-full"
                                    style={{ height: '100%' }}
                                    animate={{ scaleY: [0.3, 1, 0.5, 0.85, 0.4] }}
                                    transition={{
                                        duration: 1.6,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                        delay,
                                    }}
                                />
                            ))}
                        </span>
                        QubbyAI Console · Establishing Connection
                        <motion.span
                            className="text-accent-700"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            ▮
                        </motion.span>
                    </motion.div>

                    <div className="max-w-xl">
                        <motion.img
                            variants={fadeUp}
                            src="/qubby-icon.png"
                            alt="QubbyAI"
                            className="h-24 w-24 mb-6 select-none"
                            draggable={false}
                        />
                        <motion.h1
                            variants={fadeUp}
                            className="heading-display font-semibold text-ink-900 tracking-[-0.02em] leading-[1.05] text-[clamp(2.5rem,4.4vw,4.25rem)] [word-break:keep-all]"
                        >
                            <span className="inline">
                                QubbyAI <span className="text-accent-700 italic">{t('hero.brandAccent')}</span>
                            </span>
                            <br />
                            <span className="inline">{t('hero.brandLine2')}</span>
                        </motion.h1>
                        <motion.p
                            variants={fadeUp}
                            className="text-[17px] text-ink-500 mt-8 leading-relaxed max-w-md"
                        >
                            {t('hero.desc')}
                        </motion.p>
                        <motion.div variants={fadeUp} className="mt-12">
                            <StatsTrio />
                        </motion.div>
                    </div>

                    <motion.div
                        variants={fadeUp}
                        className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400"
                    >
                        <span>光禾感知科技</span>
                        <span className="h-3 w-px bg-cream-300" />
                        <span>OSENSE</span>
                        <span className="h-3 w-px bg-cream-300" />
                        <span className="text-accent-700">v1.0.0</span>
                        <span className="h-3 w-px bg-cream-300" />
                        <span>Made in Taipei</span>
                    </motion.div>
                </motion.div>

                {/* RIGHT — auth form */}
                <div className="flex items-center justify-center px-6 py-16 lg:px-12 xl:pl-8 xl:pr-20">
                    <motion.div
                        className="w-full max-w-md xl:max-w-[420px]"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                    >
                        {/* Mobile-only compact brand */}
                        <div className="lg:hidden text-center mb-10">
                            <img src="/qubby-icon.png" alt="QubbyAI" className="h-16 w-16 mx-auto mb-3 select-none" draggable={false} />
                            <div className="heading-display text-5xl font-semibold text-ink-900 tracking-tight leading-none">
                                QubbyAI
                            </div>
                            <div className="heading-display text-xl text-ink-500 mt-2">
                                {t('hero.mobileSub')}
                            </div>
                        </div>

                        <motion.div
                            className="relative bg-white/85 backdrop-blur-md border border-cream-200 rounded-2xl shadow-[0_2px_24px_-12px_rgba(28,26,23,0.12)] p-10"
                            whileHover={{ boxShadow: '0 8px 40px -16px rgba(28,26,23,0.2)' }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Accent vertical bar on left side */}
                            <span className="absolute left-0 top-10 bottom-10 w-[3px] rounded-r-full bg-gradient-to-b from-accent-400 via-accent-500 to-accent-300" />
                            <div className="flex items-center gap-2 mb-3">
                                <motion.span
                                    className="inline-flex items-center justify-center size-7 rounded-full bg-accent-100 text-accent-700 shrink-0"
                                    animate={{ rotate: [0, -8, 8, -6, 6, 0] }}
                                    transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
                                >
                                    <PhoneCall size={13} strokeWidth={2} />
                                </motion.span>
                                <h1 className="heading-display text-3xl font-semibold text-ink-900 tracking-tight">
                                    {t('title')}
                                </h1>
                            </div>
                            <p className="text-sm text-ink-500 mb-8 leading-relaxed">
                                {t('subtitle')}
                            </p>

                            <form onSubmit={onSubmit} className="space-y-4">
                                <div className="relative">
                                    <Mail size={14} className="absolute left-3 top-9 text-ink-400 pointer-events-none" />
                                    <Input
                                        label="Email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-9"
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        required
                                    />
                                </div>
                                <div className="relative">
                                    <Lock size={14} className="absolute left-3 top-9 text-ink-400 pointer-events-none" />
                                    <Input
                                        label={t('password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-9 pr-10"
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        aria-label={showPassword ? t('password.hide') : t('password.show')}
                                        title={showPassword ? t('password.hide') : t('password.show')}
                                        className="absolute right-2 top-7 p-1.5 text-ink-400 hover:text-ink-700 transition rounded hover:bg-cream-100"
                                    >
                                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>

                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 text-xs text-ink-500 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={remember}
                                            onChange={(e) => setRemember(e.target.checked)}
                                            className="size-3.5 accent-accent-500"
                                        />
                                        {t('rememberEmail')}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={onForgotPassword}
                                        disabled={resetting}
                                        className="text-xs text-accent-700 hover:underline disabled:opacity-50"
                                    >
                                        {resetting ? t('forgot.sending') : t('forgot')}
                                    </button>
                                </div>

                                {/* 1020 業務需求：登入前必須同意使用者政策（required → 瀏覽器原生擋送出；每次登入都要勾，不記憶）。
                                    agree.prefix 不可留空：i18n 設 returnEmptyString:false，空字串會退回中文 */}
                                <label className="flex items-start gap-2 text-xs text-ink-600 cursor-pointer select-none">
                                    <input type="checkbox" required className="size-3.5 mt-px accent-accent-500" />
                                    <span>
                                        {t('agree.prefix')}
                                        <a href="https://home.qubby.io/policy" target="_blank" rel="noopener noreferrer" className="text-accent-700 underline underline-offset-2 hover:text-accent-500">
                                            {t('agree.policy')}
                                        </a>
                                        {t('agree.and')}
                                        <a href="https://home.qubby.io/terms" target="_blank" rel="noopener noreferrer" className="text-accent-700 underline underline-offset-2 hover:text-accent-500">
                                            {t('agree.terms')}
                                        </a>
                                    </span>
                                </label>

                                {resetMsg && (
                                    <div className="px-3 py-2 bg-success-100/60 border border-success-500/30 text-success-700 text-xs rounded-md">
                                        {resetMsg}
                                    </div>
                                )}

                                <AnimatePresence mode="wait">
                                    {error && (
                                        <motion.div
                                            key="error"
                                            initial={{ opacity: 0, y: -8, scale: 0.96 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.96 }}
                                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                            className="px-3 py-2 bg-danger-100/60 border border-danger-500/30 text-danger-500 text-xs rounded-md"
                                        >
                                            {error}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <motion.div whileTap={{ scale: 0.98 }} whileHover={{ y: -1 }}>
                                    <Button
                                        type="submit"
                                        disabled={submitting || loading}
                                        variant="primary"
                                        className="w-full !px-4 !py-3 !text-base"
                                    >
                                        {submitting ? (
                                            <span className="inline-flex items-center gap-2">
                                                <motion.span
                                                    className="inline-block size-3 rounded-full border-2 border-current border-r-transparent"
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                                                />
                                                {t('submitting')}
                                            </span>
                                        ) : (
                                            t('submit')
                                        )}
                                    </Button>
                                </motion.div>
                            </form>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

/**
 * Top operator status bar — system console 風格頂部 bar
 * 左：mono 品牌 + 脈動點。中：系統 health pill。右：即時時鐘
 */
function TopStatusBar() {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    return (
        <motion.div
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 z-20 px-6 lg:px-10 py-3.5 pointer-events-none"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em]">
                {/* Left — brand mark */}
                <div className="inline-flex items-center gap-2 text-ink-600">
                    <motion.span
                        className="size-1.5 rounded-full bg-accent-500"
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    QubbyAI · Operator Console
                </div>

                {/* Center — system health (hidden on small screens) */}
                <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-success-100/70 border border-success-500/30 text-success-500">
                    <motion.span
                        className="size-1.5 rounded-full bg-success-500"
                        animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <ShieldCheck size={10} strokeWidth={2.4} />
                    All Systems Operational
                </div>

                {/* Right — live clock */}
                <div className="inline-flex items-center gap-2 text-ink-500 tabular-nums">
                    <span className="hidden sm:inline">{dateStr}</span>
                    <span className="text-ink-700">{timeStr}</span>
                    <span className="text-ink-300 hidden sm:inline">TST</span>
                </div>
            </div>
        </motion.div>
    );
}

/**
 * CardConnector — 兩張卡之間的虛線路徑 + 流動小點，視覺暗示 call routing
 * 桌面 (xl) 才顯示，定位避開 brand 文字區
 */
function CardConnector() {
    return (
        <svg
            aria-hidden="true"
            className="hidden xl:block absolute pointer-events-none"
            style={{ left: '32%', top: '24%', width: '120px', height: '320px', zIndex: 9 }}
            viewBox="0 0 120 320"
            fill="none"
        >
            <motion.path
                d="M 90 10 Q 30 80 60 160 Q 90 240 30 310"
                stroke="var(--color-accent-400)"
                strokeWidth="1"
                strokeDasharray="3 5"
                fill="none"
                opacity="0.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 1.2, ease: 'easeInOut' }}
            />
            {/* 流動的小光點 — 沿路徑 loop */}
            <motion.circle
                r="2.5"
                fill="var(--color-accent-500)"
                animate={{
                    offsetDistance: ['0%', '100%'],
                    opacity: [0, 1, 1, 0],
                }}
                transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    times: [0, 0.15, 0.85, 1],
                    delay: 2,
                }}
                style={{
                    offsetPath: 'path("M 90 10 Q 30 80 60 160 Q 90 240 30 310")',
                    offsetRotate: '0deg',
                }}
            />
        </svg>
    );
}

/**
 * Light beams — 2 道 vertical data-stream 光束從上往下淡，疊在 aurora 之上加深度
 */
function LightBeams() {
    return (
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div
                className="absolute top-0 bottom-0 w-px"
                style={{
                    left: '32%',
                    background: 'linear-gradient(to bottom, transparent 0%, var(--color-accent-400) 12%, var(--color-accent-300) 40%, transparent 100%)',
                    opacity: 0.4,
                }}
                animate={{ opacity: [0.25, 0.55, 0.25] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
                className="absolute top-0 bottom-0 w-px"
                style={{
                    left: '68%',
                    background: 'linear-gradient(to bottom, transparent 0%, var(--color-accent-300) 20%, transparent 80%)',
                    opacity: 0.3,
                }}
                animate={{ opacity: [0.18, 0.42, 0.18] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            />
        </div>
    );
}

/**
 * Stats trio — 三組大數字 + 標籤，數字 mount 時 count-up 1.5s
 */
function StatsTrio() {
    const { t } = useTranslation('login');
    const stats = [
        { value: 100, suffix: '%', label: t('stats.automation'), sub: 'Hands-free' },
        { value: 71.3, suffix: '', label: t('stats.intelligence'), sub: 'Intelligence Index', decimals: 1 },
        { value: 38, suffix: '', label: t('stats.languages'), sub: 'Languages' },
    ];
    return (
        <div className="grid grid-cols-3 gap-x-10 gap-y-1 max-w-md">
            {stats.map((s, i) => (
                <div key={s.label} className="flex flex-col">
                    <div className="heading-display text-[2.25rem] font-semibold text-ink-900 tracking-tight tabular-nums leading-none">
                        <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} delay={400 + i * 120} />
                    </div>
                    <div className="mt-2 text-[11px] text-ink-700 font-medium">{s.label}</div>
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-ink-400 mt-0.5">{s.sub}</div>
                </div>
            ))}
        </div>
    );
}

/**
 * CountUp — easeOutCubic 從 start → end，duration ms 內跑完
 */
function CountUp({ end, start = 0, duration = 1500, suffix = '', decimals = 0, delay = 0 }) {
    const [val, setVal] = useState(start);
    useEffect(() => {
        const startTs = Date.now() + delay;
        let raf;
        const tick = () => {
            const elapsed = Date.now() - startTs;
            if (elapsed < 0) { raf = requestAnimationFrame(tick); return; }
            const t = Math.min(1, elapsed / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(start + (end - start) * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [end, start, duration, delay]);
    const fmt = decimals > 0
        ? val.toFixed(decimals)
        : Math.round(val).toLocaleString();
    return <>{fmt}{suffix}</>;
}

/**
 * Floating call card — Aircall / Dialpad 風的浮動通話 UI
 * - 桌面才顯示（xl ≥1280px），定位在左 brand panel 之上
 * - direction='outbound': 主動撥出（紅色 EndCall 按鈕脈動）
 * - direction='inbound':  來電響鈴（綠色 Answer 按鈕脈動）
 */
const CALL_VARIANTS = {
    outbound: {
        label: 'Outbound · Calling',
        Icon: PhoneOutgoing,
        name: '王先生',
        avatar: '王',
        phone: '+886 981-968-022',
        accentClass: 'text-accent-700',
        dotClass: 'bg-accent-500',
        avatarGradient: 'from-accent-200 via-accent-300 to-accent-400',
        waveformGradient: 'from-accent-300 to-accent-600',
        glowColor: 'var(--color-accent-300)',
        actionBg: 'bg-danger-500',
        actionIcon: PhoneOutgoing,
        actionRotate: 135,
    },
    inbound: {
        label: 'Inbound · Ringing',
        Icon: PhoneIncoming,
        name: '陳小姐',
        avatar: '陳',
        phone: '+886 2 2706-3171',
        accentClass: 'text-success-500',
        dotClass: 'bg-success-500',
        avatarGradient: 'from-success-100 via-success-300 to-success-500',
        waveformGradient: 'from-success-300 to-success-500',
        glowColor: 'rgba(74, 159, 124, 0.45)',
        actionBg: 'bg-success-500',
        actionIcon: Phone,
        actionRotate: 0,
    },
};

function FloatingCallCard({ direction = 'outbound', position = {}, rotate = 0, z = 10, delay = 0.55, muted = false }) {
    const { t } = useTranslation('login');
    const base = CALL_VARIANTS[direction] || CALL_VARIANTS.outbound;
    // 範例聯絡人姓名 / 頭像字 跟著介面語系（裝飾用，aria-hidden）
    const v = {
        ...base,
        name: t(`demo.${direction}.name`, base.name),
        avatar: t(`demo.${direction}.avatar`, base.avatar),
    };
    const { Icon, actionIcon: ActionIcon } = v;

    // pre-compute 24 條波形棒的起始高度跟 delay，避免每 render re-randomize
    const bars = useMemo(
        () =>
            Array.from({ length: 24 }, (_, i) => ({
                base: 25 + Math.random() * 30,
                peak: 55 + Math.random() * 45,
                delay: i * 0.045,
            })),
        []
    );

    // 模擬秒數 ticking（純視覺）
    // outbound: 已通 0:07 開始 tick
    // inbound: 還在響鈴，timer 顯示「---」
    const [tick, setTick] = useState(direction === 'outbound' ? 7 : 0);
    useEffect(() => {
        if (direction !== 'outbound') return;
        const t = setInterval(() => setTick((v) => (v + 1) % 60), 1000);
        return () => clearInterval(t);
    }, [direction]);
    const mm = String(Math.floor(tick / 60)).padStart(2, '0');
    const ss = String(tick % 60).padStart(2, '0');
    const timerLabel = direction === 'outbound' ? `${mm}:${ss}` : 'Ringing…';

    return (
        <motion.div
            aria-hidden="true"
            className="hidden xl:block absolute pointer-events-none"
            style={{
                ...position,
                zIndex: z,
                maxWidth: '268px',
                opacity: muted ? 0.9 : 1,
            }}
            initial={{ opacity: 0, y: 28, rotate: rotate - 2 }}
            animate={{ opacity: muted ? 0.9 : 1, y: 0, rotate }}
            transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            <motion.div
                className={`relative w-[268px] rounded-2xl ${
                    muted ? 'bg-white/92' : 'bg-white/95'
                } backdrop-blur-xl border border-cream-200 ${
                    muted
                        ? 'shadow-[0_18px_44px_-22px_rgba(28,26,23,0.22),0_3px_10px_-4px_rgba(28,26,23,0.08)]'
                        : 'shadow-[0_24px_60px_-24px_rgba(28,26,23,0.25),0_4px_12px_-4px_rgba(28,26,23,0.08)]'
                } p-4`}
                animate={{ y: direction === 'outbound' ? [0, -8, 0] : [0, -6, 0] }}
                transition={{
                    duration: direction === 'outbound' ? 5 : 6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: direction === 'outbound' ? 0 : 0.8,
                }}
            >
                {/* 角落漸層 glow */}
                <motion.div
                    className="absolute -top-px -left-px h-12 w-24 rounded-tl-2xl pointer-events-none"
                    style={{
                        background: `radial-gradient(120% 100% at 0% 0%, ${v.glowColor} 0%, transparent 70%)`,
                        opacity: 0.55,
                    }}
                    animate={{ opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Status pill */}
                <div className="flex items-center justify-between mb-4">
                    <div className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] ${v.accentClass}`}>
                        <motion.span
                            className={`size-1.5 rounded-full ${v.dotClass}`}
                            animate={{ opacity: [1, 0.25, 1], scale: [1, 1.3, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        {v.label}
                    </div>
                    <Icon size={13} strokeWidth={2} className={v.accentClass} />
                </div>

                {/* Contact */}
                <div className="flex items-center gap-3">
                    <div className={`size-11 rounded-full bg-gradient-to-br ${v.avatarGradient} grid place-items-center text-ink-900 font-semibold text-base shrink-0 ring-2 ring-white`}>
                        {v.avatar}
                    </div>
                    <div className="min-w-0">
                        <div className="text-ink-900 font-medium text-sm truncate">{v.name}</div>
                        <div className="text-[11px] text-ink-500 font-mono tabular-nums tracking-tight">
                            {v.phone}
                        </div>
                    </div>
                </div>

                {/* Waveform — 24 條 oscillating bars（inbound 用較弱起伏模擬等待） */}
                <div className="mt-4 flex items-end gap-[2px] h-9 px-0.5">
                    {bars.map((b, i) => {
                        const isWaiting = direction === 'inbound';
                        return (
                            <motion.span
                                key={i}
                                className={`flex-1 rounded-full bg-gradient-to-t ${v.waveformGradient}`}
                                style={{ minHeight: '3px' }}
                                animate={{
                                    height: isWaiting
                                        ? [`${b.base * 0.6}%`, `${b.base * 1.2}%`, `${b.base * 0.6}%`]
                                        : [`${b.base}%`, `${b.peak}%`, `${b.base}%`],
                                    opacity: isWaiting ? [0.35, 0.7, 0.35] : [0.55, 1, 0.55],
                                }}
                                transition={{
                                    duration: isWaiting ? 1.8 : 1.1 + (i % 5) * 0.1,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                    delay: b.delay,
                                }}
                            />
                        );
                    })}
                </div>

                {/* Timer + controls */}
                <div className="mt-4 flex items-center justify-between">
                    <div className="font-mono text-[11px] text-ink-500 tabular-nums tracking-wide">
                        {timerLabel}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="size-6 rounded-full bg-cream-100 grid place-items-center text-ink-500">
                            <Mic size={11} strokeWidth={2} />
                        </span>
                        <span className="size-6 rounded-full bg-cream-100 grid place-items-center text-ink-300">
                            <MicOff size={11} strokeWidth={2} />
                        </span>
                        <motion.span
                            className={`size-6 rounded-full ${v.actionBg} grid place-items-center text-white ml-1 shadow-[0_2px_8px_rgba(0,0,0,0.15)]`}
                            animate={{ scale: [1, direction === 'inbound' ? 1.08 : 0.94, 1] }}
                            transition={{
                                duration: direction === 'inbound' ? 1.1 : 1.8,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                        >
                            <ActionIcon size={11} strokeWidth={2.4} style={{ transform: `rotate(${v.actionRotate}deg)` }} />
                        </motion.span>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
